import React, { type MouseEvent, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/standard-button';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { useIntl } from '@atlassian/jira-intl';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import {
	ADD_PRIORITY_SCHEME_BUTTON_SELECTOR,
	ADD_PRIORITY_SCHEME_BUTTON_LIMIT_EXCEEDED_TOOLTIP_SELECTOR,
} from '../../common/constants';
import { PRIORITY_SCHEMES_HARD_LIMIT } from '../../common/ui/constants';
import { HeaderDescription } from './header-description';
import { messages } from './messages';

type AddPrioritySchemeButtonProps = {
	isDisabled: boolean;
	onAddScheme: (_event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
};

type ConditionalTooltipProps = {
	children: ReactNode;
	isLocked: boolean;
	limitSchemes: boolean;
	tooltipDelay?: number;
};

type PrioritySchemesHeaderProps = {
	hasError: boolean;
	isLoading: boolean;
	isLocked: boolean;
	limitSchemes: boolean;
	onAddScheme: (_event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
};

export const AddPrioritySchemeButton = ({
	isDisabled,
	onAddScheme,
}: AddPrioritySchemeButtonProps) => {
	const { formatMessage } = useIntl();

	return (
		<Button
			isDisabled={isDisabled}
			onClick={onAddScheme}
			testId={ADD_PRIORITY_SCHEME_BUTTON_SELECTOR}
		>
			{formatMessage(messages.addPriorityScheme)}
		</Button>
	);
};

export const ConditionalTooltip = ({
	children,
	isLocked,
	limitSchemes,
	tooltipDelay,
}: ConditionalTooltipProps) => {
	const { formatMessage } = useIntl();

	if (isLocked) {
		return (
			<Tooltip content={formatMessage(messages.isLockedTooltip)} delay={tooltipDelay}>
				{children}
			</Tooltip>
		);
	}

	if (limitSchemes) {
		return (
			<Tooltip
				testId={ADD_PRIORITY_SCHEME_BUTTON_LIMIT_EXCEEDED_TOOLTIP_SELECTOR}
				content={formatMessage(messages.isHardLimitTooltip, {
					count: PRIORITY_SCHEMES_HARD_LIMIT,
				})}
				delay={tooltipDelay}
			>
				{children}
			</Tooltip>
		);
	}

	return <>{children}</>;
};

export const PrioritySchemesHeader = ({
	hasError,
	isLoading,
	isLocked,
	limitSchemes,
	onAddScheme,
}: PrioritySchemesHeaderProps) => {
	const { formatMessage } = useIntl();

	const isButtonDisabled = isLoading || isLocked || limitSchemes;

	return (
		<Container>
			<PageHeader
				actions={
					!hasError && (
						<ConditionalTooltip isLocked={isLocked} limitSchemes={limitSchemes}>
							<AddPrioritySchemeButton isDisabled={isButtonDisabled} onAddScheme={onAddScheme} />
						</ConditionalTooltip>
					)
				}
			>
				{formatMessage(messages.title)}
			</PageHeader>
			{!hasError && <HeaderDescription />}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	p: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- PLEASE FIX - ENABLING FLAT LINT CONFIG
		font: token('font.body', fontFallback.body.medium),
	},
});
